/* eslint-disable react/jsx-props-no-spreading */
// We are using a form validating lib that relies on spreading props

import React from 'react';
import { useForm } from 'react-hook-form';
import { TextInput } from 'playup-web-core';

import LoadingButton from '../../../components/LoadingButton';
import Modal from '../../../components/Modal';
import useNotify from '../../../hooks/useNotify';
import { confirmMobileCode, sendMobileVerification } from '../../../api/verification';
import { parseApiError } from '../../../utils/parseApiError';

interface Props {
	onClose: () => void;
	onRefresh: () => void;
	mobileNumber?: string;
}
interface FormData {
	code: string;
}

const MobileVerification: React.FC<Props> = ({ onClose, onRefresh, mobileNumber }) => {
	const [errors, setErrors] = React.useState<APIError>();
	const [sending, setSending] = React.useState(false);
	const [newCodeSending, setNewCodeSending] = React.useState(false);
	const [confirmOpen, setConfirmOpen] = React.useState(false);

	const { register, handleSubmit } = useForm<FormData>();
	const notify = useNotify();

	const handleSendCode = (newCode = false) => {
		setSending(!newCode);
		setNewCodeSending(!!newCode);

		sendMobileVerification()
			.then(() => {
				setSending(false);
				setNewCodeSending(false);
				setConfirmOpen(true);
				notify({
					type: 'success',
					title: 'Success',
					message: 'Please check your mobile for the code.',
					timeout: 2000,
				});
			})
			.catch((err) => {
				setSending(false);
				setNewCodeSending(false);
				setErrors(err);
			});
	};

	const onSubmitCode = (data: FormData) => {
		if (!data.code) {
			setErrors({ error: ['Please enter a code first.'] });
			return;
		}

		setSending(true);

		confirmMobileCode(data.code)
			.then(() => {
				onRefresh();
				onClose();
			})
			.catch((error) => {
				setErrors(error.errors);
			})
			.finally(() => {
				setSending(false);
			});
	};

	return (
		<Modal onClose={onClose} bg wrapperClassName="_max-w-md _mx-2 md:_mx-auto">
			<h3 className="_py-2 _text-2xl _font-extrabold _text-center _text-gray-700 _uppercase">MOBILE CONFIRMATION</h3>
			<form onSubmit={handleSubmit(onSubmitCode)}>
				<div className="_flex _flex-col _justify-center _text-center _mt-4">
					{!confirmOpen ? (
						<>
							<div>
								Your mobile number is registered as <span className="_font-semibold">{mobileNumber}</span>
							</div>
							<LoadingButton
								type="button"
								onClick={() => handleSendCode(false)}
								loading={sending}
								className="_mt-4 _mb-2 _btn _btn-primary-action"
							>
								Get Verification Code
							</LoadingButton>
						</>
					) : (
						<>
							<div>Please enter the code we&apos;ve sent to your phone</div>
							<TextInput
								kind="white"
								className="_mt-4"
								type="number"
								placeholder="CODE"
								{...register('code', { required: true })}
								error={parseApiError(errors, 'code')}
							/>
							<LoadingButton type="submit" loading={sending} className="_mt-2 _mb-2 _btn _btn-primary-action">
								Submit Code
							</LoadingButton>
							<LoadingButton
								type="button"
								onClick={() => handleSendCode(true)}
								loading={newCodeSending}
								className="_mb-2 _btn _btn-warning"
							>
								Request New Code
							</LoadingButton>
						</>
					)}
					<button className="_mb-2 _btn _btn-black" onClick={onClose}>
						Cancel
					</button>
					{errors && errors.error && <div className="_mb-3 _text-center form-error">{parseApiError(errors)}</div>}
				</div>
			</form>
		</Modal>
	);
};

export default MobileVerification;
